/** Normalise paths so stack comparisons ignore trailing slashes. */
export const normalizeRoute = (path) => {
  if (!path) return "/";
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const trimmed = withSlash.replace(/\/+$/, "");
  return trimmed || "/";
};

const cp = () => window.contextPath || "";

export const homeBreadcrumbEntry = () => ({
  id: "home",
  labelKey: "DFSWEB_HOME",
  route: normalizeRoute(`${cp()}/`) || "/",
});

export const schemesListBreadcrumbEntry = () => ({
  id: "schemes",
  labelKey: "schemes.governmentSchemes",
  route: normalizeRoute(`${cp()}/schemes`),
});

// Flat Home › Applied Scheme trail for the applied-schemes list.
export const appliedSchemesBreadcrumbEntry = () => ({
  id: "applied-schemes",
  labelKey: "schemes.applied.pageTitle",
  route: normalizeRoute(`${cp()}/schemes/applied`),
});

// Flat Home › Applied Scheme Details trail — own label, distinct from the
// list's "Applied Scheme" crumb; the details URL is not a deeper crumb.
export const applicationDetailsBreadcrumbEntry = () => ({
  id: "application-details",
  labelKey: "schemes.applied.detailsPageTitle",
  route: normalizeRoute(`${cp()}/schemes/applied/application-details`),
});

export const schemeDetailsBreadcrumbEntry = (state = {}) => ({
  id: "scheme-details",
  labelKey: "schemes.breadcrumbSchemeDetails",
  route: normalizeRoute(`${cp()}/schemes/details`),
  state: {
    schemeId: state.schemeId,
    level: state.level,
    startDate: state.startDate,
    endDate: state.endDate,
    active: state.active,
    mainId: state.mainSchemeId ?? state.mainId,
  },
});

// Members of this group are erased together (BreadcrumbStackContext's
// popGroup) once the apply flow ends — success landing back on scheme
// details/verify, not the apply/preview sub-steps.
export const SCHEME_APPLY_FORM_GROUP = "scheme-apply-form";

export const schemeApplyBreadcrumbEntry = (state = {}) => ({
  id: "scheme-apply",
  labelKey: "schemes.breadcrumbApplyScheme",
  route: normalizeRoute(`${cp()}/schemes/details/apply`),
  group: SCHEME_APPLY_FORM_GROUP,
  state: {
    schemeId: state.schemeId,
    level: state.level,
    startDate: state.startDate,
    endDate: state.endDate,
    active: state.active,
    mainId: state.mainSchemeId ?? state.mainId,
  },
});

export const accountVerificationBreadcrumbEntry = (state = {}) => ({
  id: "account-verification",
  labelKey: "ONBOARDING_ACCOUNT_VERIFICATION_BREADCRUMB",
  route: normalizeRoute(state.route ?? `${cp()}/schemes/details/apply`),
  ...(state.schemeId
    ? {
        state: {
          schemeId: state.schemeId,
          level: state.level,
          startDate: state.startDate,
          endDate: state.endDate,
          active: state.active,
          mainId: state.mainSchemeId ?? state.mainId,
        },
      }
    : {}),
});

export const schemeVerifyBreadcrumbEntry = (state = {}) => ({
  ...accountVerificationBreadcrumbEntry({
    ...state,
    route: `${cp()}/schemes/details/apply`,
  }),
  id: "scheme-verify",
  group: SCHEME_APPLY_FORM_GROUP,
});

const FEATURE_ROUTE_CRUMBS = [
  {
    match: (path) => path.includes("/farmer-passbook"),
    entry: () => ({
      id: "farmer-passbook",
      labelKey: "FARMER_PASSBOOK",
      route: normalizeRoute(`${cp()}/farmer-passbook`),
    }),
  },
  {
    match: (path) => path.includes("/grievance-redressal"),
    entry: () => ({
      id: "grievance-redressal",
      labelKey: "agentGrm.GRM",
      route: normalizeRoute(`${cp()}/grievance-redressal`),
    }),
  },
  {
    match: (path) => path.includes("/soil-card"),
    entry: () => ({
      id: "soil-card",
      labelKey: "soil_health_card",
      route: normalizeRoute(`${cp()}/soil-card`),
    }),
  },
];

export const featureBreadcrumbFromPath = (pathname = "") => {
  const path = normalizeRoute(pathname);
  const hit = FEATURE_ROUTE_CRUMBS.find(({ match }) => match(path));
  return hit ? hit.entry() : null;
};

export const accountVerificationTrailAncestors = (pathname = "", schemeState = {}) => {
  if (schemeState.schemeId) {
    return schemeApplyTrailAncestors(schemeState);
  }
  const feature = featureBreadcrumbFromPath(pathname);
  return feature ? [homeBreadcrumbEntry(), feature] : [homeBreadcrumbEntry()];
};

export const schemePreviewBreadcrumbEntry = (state = {}) => ({
  id: "scheme-preview",
  labelKey: "schemes.breadcrumbPreview",
  route: normalizeRoute(`${cp()}/schemes/details/apply`),
  group: SCHEME_APPLY_FORM_GROUP,
  state: {
    schemeId: state.schemeId,
    level: state.level,
    startDate: state.startDate,
    endDate: state.endDate,
    active: state.active,
    mainId: state.mainSchemeId ?? state.mainId,
  },
});

export const recoverDbtBreadcrumbEntry = () => ({
  id: "recover-dbt-id",
  labelKey: "Recover_DBT_ID",
  route: normalizeRoute(`${cp()}/recover-dbt-id`),
});

export const loginBreadcrumbEntry = () => ({
  id: "login",
  labelKey: "DFSWEB_LOGIN_PAGE",
  route: normalizeRoute(`${cp()}/login`),
});

export const nonDbtRegistrationBreadcrumbEntry = () => ({
  id: "non-dbt-registration",
  labelKey: "PROFILE_CREATE",
  route: normalizeRoute(`${cp()}/login`),
  guardProfile: "non-dbt-registration",
});

export const schemeApplyTrailAncestors = (state = {}) => [
  homeBreadcrumbEntry(),
  schemesListBreadcrumbEntry(),
  schemeDetailsBreadcrumbEntry(state),
];
